import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { usePortalAuthStore } from '@/store/portal-auth.store';
import type { PortalSessionToken } from '@/store/portal-auth.store';
import type {
  PortalAssessment,
  PortalAssessmentDetail,
  PortalCertification,
  PortalTeamMember,
} from '@/api/types/assessment.types';

// ── Sprint U.1 invitation metadata (GET /portal/auth/invitation/:token) ──────

export interface InvitationMetadata {
  branch_type: 'existing_account' | 'soft_delete_restored' | 'new_registration';
  tenant_name: string;
  tenant_logo_url: string | null;
  vendor_account_email_masked: string;
  expires_at: string;
}

export function useInvitationMetadata(token: string) {
  return useQuery({
    queryKey: ['portal-invitation', token],
    queryFn: async () => {
      const { data } = await apiClient.get(`/portal/auth/invitation/${encodeURIComponent(token)}`);
      return data.data as InvitationMetadata;
    },
    enabled: !!token,
    retry: false,
  });
}

// ── Portal assessments ────────────────────────────────────────────────────────

export function usePortalAssessments() {
  const { isPortalAuthenticated } = usePortalAuthStore();
  return useQuery({
    queryKey: ['portal-assessments'],
    queryFn: async () => {
      const { data } = await apiClient.get('/portal/assessments');
      return data.data as PortalAssessment[];
    },
    enabled: isPortalAuthenticated,
    staleTime: 30_000,
  });
}

export function usePortalAssessment(id: string | undefined) {
  const { isPortalAuthenticated } = usePortalAuthStore();
  return useQuery({
    queryKey: ['portal-assessment', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/portal/assessments/${id}`);
      return data.data as PortalAssessmentDetail;
    },
    enabled: !!id && isPortalAuthenticated,
    staleTime: 15_000,
  });
}

export function usePortalTenantAssessments(tenantSlug: string | undefined) {
  const { isPortalAuthenticated } = usePortalAuthStore();
  return useQuery({
    queryKey: ['portal-tenant-assessments', tenantSlug],
    queryFn: async () => {
      const { data } = await apiClient.get(`/portal/tenants/${encodeURIComponent(tenantSlug!)}/assessments`);
      return data.data as PortalAssessment[];
    },
    enabled: isPortalAuthenticated && !!tenantSlug,
    staleTime: 30_000,
  });
}

// ── Bulk save responses (auto-save + save draft) ──────────────────────────────

export function useSaveResponses(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (responses: Array<{ questionId: string; responseValue: string; vendorClarification?: string }>) => {
      const { data } = await apiClient.patch(
        `/portal/assessments/${assessmentId}/responses`,
        { responses },
      );
      return data.data as { saved: boolean; completionPct: number };
    },
    onSuccess: () => {
      // Invalidate the detail cache so section remounts get fresh response_* props.
      // Also invalidate list caches so answered_count + status reflect the save.
      // refetchOnWindowFocus is disabled globally, so explicit invalidation is the
      // only mechanism that keeps list/detail in sync after a save.
      qc.invalidateQueries({ queryKey: ['portal-assessment', assessmentId] });
      qc.invalidateQueries({ queryKey: ['portal-assessments'] });
      qc.invalidateQueries({ queryKey: ['portal-tenant-assessments'] });
    },
  });
}

// ── Submit assessment ─────────────────────────────────────────────────────────

export function useSubmitAssessment(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(`/portal/assessments/${assessmentId}/submit`);
      if (!data.data?.submitted) throw new Error(data.errors?.message ?? 'Submit failed');
      return data.data as { submitted: boolean; submittedAt: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-assessment', assessmentId] });
      qc.invalidateQueries({ queryKey: ['portal-assessments'] });
    },
  });
}

// ── VA.3.7: vendor bypass attestation ────────────────────────────────────────

export function useBypassAttest(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { question_ids: string[]; certification_ids: string[] }) => {
      const { data } = await apiClient.post(`/portal/assessments/${assessmentId}/bypass-attest`, dto);
      return data.data as { questionsApplied: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-assessment', assessmentId] });
      qc.invalidateQueries({ queryKey: ['portal-assessments'] });
    },
  });
}

// ── Download assessment PDF ────────────────────────────────────────────────────

export async function downloadAssessmentPdf(assessmentId: string, vendorName: string): Promise<void> {
  const response = await apiClient.get(`/portal/assessments/${assessmentId}/download`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  const dateStr = new Date().toISOString().substring(0, 10);
  link.setAttribute('download', `assessment-${vendorName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${dateStr}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ── Certifications ────────────────────────────────────────────────────────────

export function usePortalCertifications() {
  const { isPortalAuthenticated } = usePortalAuthStore();
  return useQuery({
    queryKey: ['portal-certifications'],
    queryFn: async () => {
      const { data } = await apiClient.get('/portal/certifications');
      return data.data as PortalCertification[];
    },
    enabled: isPortalAuthenticated,
    staleTime: 2 * 60_000,
  });
}

// Metadata-only cert create (S3): documents are NOT uploaded — a JSON payload, no multipart.
export function useUploadCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await apiClient.post('/portal/certifications', payload);
      return data.data as PortalCertification;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-certifications'] });
    },
  });
}

export function useWithdrawCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ certId, withdrawal_reason }: { certId: string; withdrawal_reason?: string | null }) => {
      const { data } = await apiClient.post(
        `/portal/certifications/${certId}/withdraw`,
        { withdrawal_reason: withdrawal_reason ?? null },
      );
      return data.data as { certId: string; tenantsArchivedCount: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-certifications'] });
    },
  });
}

// ── Team / Profile ────────────────────────────────────────────────────────────

export function usePortalTeam() {
  const { isPortalAuthenticated } = usePortalAuthStore();
  return useQuery({
    queryKey: ['portal-team'],
    queryFn: async () => {
      const { data } = await apiClient.get('/portal/profile');
      return data.data as { team: PortalTeamMember[]; vendor_name: string };
    },
    enabled: isPortalAuthenticated,
    staleTime: 2 * 60_000,
  });
}

export function useInviteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: {
      email: string;
      first_name: string;
      last_name: string;
      contact_role?: string;
    }) => {
      const { data } = await apiClient.post('/portal/team/invite', dto);
      return data.data as { contactId: string; invitationSent: boolean };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-team-members'] });
    },
  });
}

// ── User self-management ──────────────────────────────────────────────────────

export interface PortalMe {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  phone_number: string | null;
  job_title: string | null;
  mfa_enabled: boolean;
  mfa_enrolled_at: string | null;
}

export function usePortalMe() {
  const { isPortalAuthenticated } = usePortalAuthStore();
  return useQuery({
    queryKey: ['portal-me'],
    queryFn: async () => {
      const { data } = await apiClient.get('/portal/users/me');
      return data.data as PortalMe;
    },
    enabled: isPortalAuthenticated,
    staleTime: 60_000,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: {
      first_name?: string;
      last_name?: string;
      phone_number?: string;
      job_title?: string;
    }) => {
      const { data } = await apiClient.patch('/portal/users/me', dto);
      return data.data as PortalMe;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-me'] });
    },
  });
}

export function useUpdatePassword() {
  return useMutation({
    mutationFn: async (dto: { current_password: string; new_password: string }) => {
      const { data } = await apiClient.patch('/portal/users/me/password', dto);
      return data.data as { updated: boolean };
    },
  });
}

export interface PortalTeamContact {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role_title: string | null;
  status: string;
  accepted_at: string | null;
  invitation_sent_at: string | null;
  mfa_enabled: boolean | null;
  last_login_at: string | null;
  is_admin: boolean;
}

export function usePortalTeamMembers() {
  const { isPortalAuthenticated } = usePortalAuthStore();
  return useQuery({
    queryKey: ['portal-team-members'],
    queryFn: async () => {
      const { data } = await apiClient.get('/portal/team');
      return data.data as PortalTeamContact[];
    },
    enabled: isPortalAuthenticated,
    staleTime: 30_000,
  });
}

export function useRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: string) => {
      const { data } = await apiClient.delete(`/portal/team/${contactId}`);
      return data.data as { removed: boolean };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-team-members'] });
    },
  });
}

export function useResetMfaSelf() {
  return useMutation({
    mutationFn: async (dto: { password: string }) => {
      const { data } = await apiClient.post('/portal/auth/mfa/reset-self', dto);
      return data.data as { setupToken: string };
    },
  });
}

// ── Accept / decline invitation (Sprint U.1) ─────────────────────────────────

export interface PortalEnrollmentRequiredData {
  mfaEnrollmentRequired: true;
  setupToken: string;
  expiresAt: string;
}

export function useAcceptInvitation(token: string) {
  return useMutation({
    mutationFn: async (dto: { password?: string }) => {
      const { data } = await apiClient.post(`/portal/auth/invitation/${encodeURIComponent(token)}/accept`, dto);
      return data.data as { redirect_to: string; message: string; tenant_name: string };
    },
  });
}

export function useDeclineInvitation(token: string) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(`/portal/auth/invitation/${encodeURIComponent(token)}/decline`);
      return data.data as { message: string };
    },
  });
}

// ── MFA enrollment hooks (Sprint T) ──────────────────────────────────────────

export function usePortalMfaEnrollmentStart() {
  return useMutation({
    mutationFn: async (setupToken: string) => {
      const { data } = await apiClient.post('/portal/auth/mfa/enrollment/start', { setupToken });
      if (!data.data) throw new Error('Enrollment start failed');
      return data.data as { secret: string; otpauthUrl: string };
    },
  });
}

export function usePortalMfaEnrollmentConfirm() {
  const { login } = usePortalAuthStore();
  return useMutation({
    mutationFn: async (body: { setupToken: string; totpCode: string }) => {
      const { data } = await apiClient.post('/portal/auth/mfa/enrollment/confirm', body);
      if (!data.data) throw new Error('Enrollment confirmation failed');
      const result = data.data as { sessionToken: string; vendor: { id: string; email: string } };
      return { ...result, sessionToken: result.sessionToken as PortalSessionToken };
    },
    onSuccess: (data) => {
      login(data.sessionToken, data.vendor.id);
    },
  });
}
